import { api } from './http';
import type { EnrolOptions, EnrolInput, EnrolResult, Member, MemberDevice, MemberInput, MemberSecurityInput, MemberStats } from './types';

export const members = {
  list: (signal?: AbortSignal) =>
    api.get<{ members: Member[]; stats: MemberStats }>('/members', signal),
  get: (id: number) => api.get<{ member: Member }>(`/members/${id}`),
  create: (f: MemberInput) => api.post<{ member: Member }>('/members', f),
  update: (id: number, f: MemberInput) => api.put<{ member: Member }>(`/members/${id}`, f),
  remove: (id: number) => api.del<{ ok: true }>(`/members/${id}`),
  /** Activate, suspend, expire or delete a selection in one request. */
  bulk: (action: 'activate' | 'suspend' | 'expire' | 'delete', ids: number[]) =>
    api.post<{ ok: true; changed: number }>('/members/bulk', { action, ids }),
  /** Every call is written to the audit trail, shown or not. */
  password: (id: number) =>
    api.get<{ password: string; reason: string }>(`/members/${id}/password`),
  /** Device seats, and switching two-step sign-in off. Audited. */
  security: (id: number, f: MemberSecurityInput) =>
    api.put<{ member: Member }>(`/members/${id}/security`, f),
  /** The devices they are signed in on, and the limit that applies (0 = none). */
  sessions: (id: number, signal?: AbortSignal) =>
    api.get<{ devices: MemberDevice[]; limit: number }>(`/members/${id}/sessions`, signal),
  /** Confirm (true) or take back (false) a member's email or mobile. Audited. */
  verify: (id: number, f: { email?: boolean; mobile?: boolean }) =>
    api.put<{ member: Member }>(`/members/${id}/verified`, f),
  /** What a member can be added to, with each price. */
  enrolOptions: () => api.get<EnrolOptions>('/members/enrol-options'),
  /** Add to a pass, course or batch. With `pay`, one paid line goes into Payments. */
  enrol: (id: number, f: EnrolInput) => api.post<EnrolResult>(`/members/${id}/enrol`, f),
  /** A one-minute link that opens the member area as this member, read-only. Audited. */
  switch: (id: number) => api.post<{ url: string }>(`/members/${id}/switch`, {}),
  /** Sign one device out, or 'all' of them. Audited. */
  signOut: (id: number, device: string | 'all') =>
    api.del<{ ok: true; devices: MemberDevice[] }>(`/members/${id}/sessions/${encodeURIComponent(device)}`),
};
